/**
 * SystemDesignViewer -- read-only re-render of a stored system-design
 * scene in the recruiter Interview Review.
 *
 * Accepts the scene shape produced by SystemDesignPane:
 *   { nodes: { id, kind, label, position }[], edges: { id, source, target, label? }[] }
 *
 * Renders the same domain-coloured node cards via @xyflow/react. Pan +
 * zoom + minimap stay enabled (recruiter can navigate complex sketches)
 * but every editor affordance is disabled.
 */
import React, { useMemo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Smartphone,
  Globe,
  Server,
  Database,
  Layers,
  Cloud,
  Inbox,
  HardDrive,
  Square,
  StickyNote,
  Diamond,
  Boxes,
} from 'lucide-react';

type ComponentKind =
  | 'client'
  | 'load_balancer'
  | 'service'
  | 'cache'
  | 'database'
  | 'queue'
  | 'storage'
  | 'external'
  // Generic labeled box used by domain framework templates (consulting,
  // marketing, sales, finance) where a tech-specific icon would be
  // misleading. Live pane palette also exposes this directly.
  | 'box'
  // Cross-domain primitives -- yellow sticky, decision diamond, OO class.
  | 'sticky'
  | 'diamond'
  | 'class_box';

type Shape =
  | 'rect'
  | 'pill'
  | 'cylinder'
  | 'tape'
  | 'cloud'
  | 'dashed'
  | 'sticky'
  | 'diamond'
  | 'class_box';

interface PaletteItem {
  icon: React.ComponentType<{ className?: string }>;
  shape: Shape;
  tone: { bg: string; ring: string; icon: string; hex: string };
}

const PALETTE: Record<ComponentKind, PaletteItem> = {
  client:        { icon: Smartphone, shape: 'pill',     tone: { bg: 'bg-sky-50',     ring: 'ring-sky-200',     icon: 'text-sky-600',     hex: '#e0f2fe' } },
  load_balancer: { icon: Layers,     shape: 'pill',     tone: { bg: 'bg-indigo-50',  ring: 'ring-indigo-200',  icon: 'text-indigo-600',  hex: '#e0e7ff' } },
  service:       { icon: Server,     shape: 'rect',     tone: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', icon: 'text-emerald-600', hex: '#d1fae5' } },
  cache:         { icon: HardDrive,  shape: 'cylinder', tone: { bg: 'bg-amber-50',   ring: 'ring-amber-200',   icon: 'text-amber-600',   hex: '#fef3c7' } },
  database:      { icon: Database,   shape: 'cylinder', tone: { bg: 'bg-rose-50',    ring: 'ring-rose-200',    icon: 'text-rose-600',    hex: '#ffe4e6' } },
  queue:         { icon: Inbox,      shape: 'tape',     tone: { bg: 'bg-purple-50',  ring: 'ring-purple-200',  icon: 'text-purple-600',  hex: '#f3e8ff' } },
  storage:       { icon: Cloud,      shape: 'cloud',    tone: { bg: 'bg-cyan-50',    ring: 'ring-cyan-200',    icon: 'text-cyan-600',    hex: '#cffafe' } },
  external:      { icon: Globe,      shape: 'dashed',   tone: { bg: 'bg-slate-50',   ring: 'ring-slate-200',   icon: 'text-slate-600',   hex: '#f8fafc' } },
  box:           { icon: Square,     shape: 'rect',     tone: { bg: 'bg-stone-50',   ring: 'ring-stone-200',   icon: 'text-stone-500',   hex: '#fafaf9' } },
  sticky:        { icon: StickyNote, shape: 'sticky',   tone: { bg: 'bg-yellow-100', ring: 'ring-yellow-300',  icon: 'text-yellow-700',  hex: '#fef9c3' } },
  diamond:       { icon: Diamond,    shape: 'diamond',  tone: { bg: 'bg-violet-50',  ring: 'ring-violet-200',  icon: 'text-violet-600',  hex: '#f5f3ff' } },
  class_box:     { icon: Boxes,      shape: 'class_box',tone: { bg: 'bg-teal-50',    ring: 'ring-teal-200',    icon: 'text-teal-600',    hex: '#ccfbf1' } },
};

function shapeClasses(shape: Shape): string {
  switch (shape) {
    case 'pill':     return 'rounded-full';
    case 'cylinder': return 'rounded-[6px] rounded-t-[10px]';
    case 'tape':     return 'rounded';
    case 'cloud':    return 'rounded-[14px]';
    case 'dashed':   return 'rounded-md border-dashed';
    case 'sticky':   return 'rounded-sm';
    case 'diamond':  return 'rounded-md';
    case 'class_box':return 'rounded-md';
    case 'rect':
    default:         return 'rounded';
  }
}

// Per-node size model. Live pane lets the candidate drag the edges
// to set width/height in pixels; that's what we replay here. Legacy
// scenes that pre-date this used the old `size: 'sm'|'md'|'lg'`
// preset which we approximate with a static map for backwards compat.
type LegacyNodeSize = 'sm' | 'md' | 'lg';
const LEGACY_SIZE_TO_PX: Record<LegacyNodeSize, { width: number; height: number }> = {
  sm: { width: 60, height: 20 },
  md: { width: 110, height: 32 },
  lg: { width: 180, height: 56 },
};
const DEFAULT_NODE_WIDTH = 60;
const DEFAULT_NODE_HEIGHT = 20;

function textClassForHeight(h: number): string {
  if (h < 22) return 'text-[9px] leading-none';
  if (h < 32) return 'text-[10px] leading-none';
  if (h < 48) return 'text-[11px] leading-tight';
  if (h < 80) return 'text-[13px] leading-snug';
  return 'text-[15px] leading-snug';
}
function iconClassForHeight(h: number): string {
  if (h < 22) return 'w-2 h-2';
  if (h < 32) return 'w-2.5 h-2.5';
  if (h < 48) return 'w-3 h-3';
  if (h < 80) return 'w-4 h-4';
  return 'w-[18px] h-[18px]';
}

interface NodeData extends Record<string, unknown> {
  kind: ComponentKind;
  label: string;
  width?: number;
  height?: number;
}

type ComponentNode = Node<NodeData, 'component'>;

const ReadOnlyNode: React.FC<NodeProps<ComponentNode>> = ({ data }) => {
  const palette = PALETTE[data.kind] || PALETTE.service;
  const Icon = palette.icon;
  const isCylinder = palette.shape === 'cylinder';
  const isTape = palette.shape === 'tape';
  const isCloud = palette.shape === 'cloud';
  const isSticky = palette.shape === 'sticky';
  const isDiamond = palette.shape === 'diamond';
  const isClassBox = palette.shape === 'class_box';
  const height = data.height ?? DEFAULT_NODE_HEIGHT;
  const textCls = textClassForHeight(height);
  const iconCls = iconClassForHeight(height);
  return (
    <div
      className={[
        'relative border border-white shadow-[0_1px_2px_rgba(15,23,42,0.05)] ring-1 ring-slate-200/80',
        'flex items-center px-1 py-0',
        shapeClasses(palette.shape),
        palette.tone.bg,
      ].join(' ')}
      style={{ width: '100%', height: '100%' }}
    >
      {isCylinder && (
        <span
          aria-hidden
          className={['absolute -top-1 left-2 right-2 h-2 rounded-full border border-slate-200/80', palette.tone.bg].join(' ')}
        />
      )}
      {isTape && (
        <span aria-hidden className="absolute left-1.5 top-1.5 bottom-1.5 w-1 flex flex-col justify-between">
          <span className="block h-px w-full bg-purple-300/70" />
          <span className="block h-px w-full bg-purple-300/70" />
          <span className="block h-px w-full bg-purple-300/70" />
          <span className="block h-px w-full bg-purple-300/70" />
        </span>
      )}
      {isCloud && (
        <>
          <span aria-hidden className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-cyan-50 ring-1 ring-slate-200/80" />
          <span aria-hidden className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-cyan-50 ring-1 ring-slate-200/80" />
        </>
      )}
      {isSticky && (
        <span
          aria-hidden
          className="absolute top-0 right-0 w-3 h-3 pointer-events-none"
          style={{ background: 'linear-gradient(225deg, transparent 50%, rgba(0,0,0,0.06) 50%)' }}
        />
      )}
      {isDiamond && (
        <span
          aria-hidden
          className={['absolute inset-0 pointer-events-none border border-slate-300', palette.tone.bg].join(' ')}
          style={{ transform: 'rotate(45deg) scale(0.72)', transformOrigin: 'center', borderRadius: 4 }}
        />
      )}
      {isClassBox && (
        <span
          aria-hidden
          className="absolute left-1 right-1 h-px bg-slate-300 pointer-events-none"
          style={{ top: 22 }}
        />
      )}
      <Handle type="target" position={Position.Top} isConnectable={false} className="!opacity-0" />
      <Handle type="source" position={Position.Bottom} isConnectable={false} className="!opacity-0" />
      <Handle type="target" position={Position.Left} id="l" isConnectable={false} className="!opacity-0" />
      <Handle type="source" position={Position.Right} id="r" isConnectable={false} className="!opacity-0" />
      <div className={['flex items-center gap-1 relative flex-1 min-w-0', isTape ? 'pl-2' : ''].join(' ')}>
        <Icon className={`${iconCls} flex-shrink-0 ${palette.tone.icon}`} />
        <span className={`flex-1 min-w-0 font-medium text-slate-800 truncate ${textCls}`}>{data.label}</span>
      </div>
    </div>
  );
};

const nodeTypes = { component: ReadOnlyNode };

interface SceneNode {
  id: string;
  kind: ComponentKind;
  label: string;
  width?: number;
  height?: number;
  /** Legacy preset, kept readable for scenes recorded before the
   * pixel-resize refactor. */
  size?: LegacyNodeSize;
  position: { x: number; y: number };
}
interface SceneEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}
export interface SystemDesignScene {
  nodes?: SceneNode[];
  edges?: SceneEdge[];
  version?: number;
  // Legacy textarea fallback (pre-react-flow sessions).
  notes?: string;
}

interface ViewerProps {
  scene: SystemDesignScene | null | undefined;
  height?: number;
}

const SystemDesignViewer: React.FC<ViewerProps> = ({ scene, height = 460 }) => {
  const flowNodes = useMemo<Node<NodeData>[]>(() => {
    const ns = scene?.nodes ?? [];
    return ns.map((n) => {
      // Resolve dimensions: new scenes carry explicit width/height,
      // legacy ones carry only `size`. Default to small if nothing.
      let w = n.width;
      let h = n.height;
      if ((w == null || h == null) && n.size) {
        const preset = LEGACY_SIZE_TO_PX[n.size];
        w = w ?? preset.width;
        h = h ?? preset.height;
      }
      w = w ?? DEFAULT_NODE_WIDTH;
      h = h ?? DEFAULT_NODE_HEIGHT;
      return {
        id: n.id,
        type: 'component',
        position: n.position || { x: 0, y: 0 },
        width: w,
        height: h,
        style: { width: w, height: h },
        data: { kind: n.kind, label: n.label, width: w, height: h },
        draggable: false,
        selectable: false,
      } as Node<NodeData>;
    });
  }, [scene]);

  const flowEdges = useMemo<Edge[]>(() => {
    const es = scene?.edges ?? [];
    return es.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label,
      type: 'smoothstep',
      style: { stroke: '#64748b', strokeWidth: 1.8 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b', width: 18, height: 18 },
      labelStyle: { fill: '#334155', fontSize: 11, fontWeight: 600 },
      labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 4,
    }));
  }, [scene]);

  if (flowNodes.length === 0) return null;

  return (
    <div style={{ height }} className="w-full">
      <ReactFlow<Node<NodeData>>
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        edgesFocusable={false}
        panOnDrag
        zoomOnPinch
        zoomOnDoubleClick
      >
        <Background gap={16} size={1} color="#e2e8f0" />
        <Controls showInteractive={false} className="!bg-white !border !border-slate-200 !rounded-lg !shadow-sm" />
        <MiniMap
          zoomable
          pannable
          nodeStrokeWidth={2}
          nodeColor={(node) => {
            const kind = (node.data as NodeData | undefined)?.kind || 'service';
            return PALETTE[kind]?.tone.hex || '#f1f5f9';
          }}
          className="!bg-white/70 !border !border-slate-200 !rounded-lg"
        />
      </ReactFlow>
    </div>
  );
};

const SystemDesignViewerProvider: React.FC<ViewerProps> = (props) => (
  <ReactFlowProvider>
    <SystemDesignViewer {...props} />
  </ReactFlowProvider>
);

export default SystemDesignViewerProvider;
